// src/containers/RentalTideListingPage/RentalTideListingPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { IconClose, Page } from '../../components';
import { searchRentalTideListings } from '../../util/api';

import css from './RentalTideListingPage.module.css';

const withRef = url => {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.has('ref')) u.searchParams.set('ref', 'WetRentals');
    return u.toString();
  } catch {
    return url;
  }
};

const CobrandHeader = ({ onClose }) => (
  <header className={css.header}>
    <Link to="/" className={css.cobrand}>
      <img src="/static/logo-desktop.png" alt="WetRentals" className={css.cobrandLogo} />
      <span className={css.cobrandX}>x</span>
      <img src="https://app.rentaltide.com/cropped/6.png" alt="RentalTide" className={css.cobrandLogo} />
    </Link>
    <button className={css.closeBtn} onClick={onClose} aria-label="Go back">
      <IconClose className={css.closeIcon} />
    </button>
  </header>
);

const RentalTideListingPage = () => {
  const { id } = useParams();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [showBooking, _setShowBooking] = useState(false);

  const setShowBooking = open => {
    _setShowBooking(open);
    document.body.style.overflow = open ? 'hidden' : '';
  };

  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);

  const inventoryId = id.replace(/^rt-/, '');

  useEffect(() => {
    searchRentalTideListings({})
      .then(response => {
        const match = response?.listings?.find(
          item => String(item.inventoryId) === inventoryId
        );
        setListing(match || null);
        if (!match) setError('Listing not found');
        setIsLoading(false);
      })
      .catch(() => {
        setError('Failed to load listing');
        setIsLoading(false);
      });
  }, [inventoryId]);

  const handleClose = () => history.goBack();

  if (isLoading) {
    return (
      <Page>
        <div className={css.root}>
          <CobrandHeader onClose={handleClose} />
          <div className={css.centered}>
            <div className={css.spinner} />
          </div>
        </div>
      </Page>
    );
  }

  if (error || !listing) {
    return (
      <Page>
        <Helmet><title>Not Found - WetRentals</title></Helmet>
        <div className={css.root}>
          <CobrandHeader onClose={handleClose} />
          <div className={css.centered}>
            <h2>Rental not found</h2>
            <button className={css.backButton} onClick={handleClose}>Go Back</button>
          </div>
        </div>
      </Page>
    );
  }

  const images = listing.images || [];
  const heroImage = images[0];
  const displayLocation = listing.location?.name || listing.location?.address || '';
  const currency = (listing.currency || 'CAD').toUpperCase();
  const priceUnit = listing.priceUnit === 'day' ? 'day' : 'hr';

  return (
    <Page>
      <Helmet>
        <title>{listing.inventoryName} | WetRentals</title>
        {heroImage && <meta property="og:image" content={heroImage} />}
      </Helmet>

      <div className={css.root}>
        <CobrandHeader onClose={handleClose} />

        {/* Hero */}
        <div className={css.hero} style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}>
          <div className={css.heroOverlay}>
            <div className={css.heroContent}>
              <h1 className={css.title}>{listing.inventoryName}</h1>
              {displayLocation && <p className={css.location}>{displayLocation}</p>}
              {listing.startingPrice && (
                <p className={css.price}>
                  From {new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(listing.startingPrice)} / {priceUnit}
                </p>
              )}
              <button className={css.bookNow} onClick={() => setShowBooking(true)}>
                Book Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking overlay */}
      {showBooking && (
        <div className={css.overlay} onClick={() => setShowBooking(false)}>
          <div className={css.modal} onClick={e => e.stopPropagation()}>
            <div className={css.modalBar}>
              <span className={css.modalTitle}>Book {listing.inventoryName}</span>
              <button className={css.modalClose} onClick={() => setShowBooking(false)} aria-label="Close">
                &#10005;
              </button>
            </div>
            <iframe
              src={withRef(listing.bookingUrl)}
              className={css.modalFrame}
              title={`Book ${listing.inventoryName}`}
              allow="payment; clipboard-write"
            />
          </div>
        </div>
      )}
    </Page>
  );
};

export default RentalTideListingPage;
